import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem,
	ContentTableColumn
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useEvents from "../../hooks/useEvents";
import useDeleteEvent from "../../hooks/useDeleteEvent";
import useToggleEventPublished from "../../hooks/useToggleEventPublished";
import { mapEventsToContentTableItems } from "../../utils/mapEventsToContentTableItems";

const formatDateTime = (iso?: string) => {
	if (!iso) return "—";
	const date = new Date(iso);
	if (!Number.isFinite(date.getTime())) return "—";
	return date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
};

const eventColumns: ContentTableColumn[] = [
	{
		key: "startDate",
		label: "Start date",
		render: (item) => formatDateTime(item.data?.startDate as string)
	},
	{
		key: "endDate",
		label: "End date",
		render: (item) => formatDateTime(item.data?.endDate as string)
	}
];

const Events = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);

	const { data: allEvents = [] } = useEvents();
	const { mutate: togglePublished } = useToggleEventPublished();
	const { mutate: deleteEvent, isLoading: isDeleting } = useDeleteEvent();

	const items = mapEventsToContentTableItems(allEvents);

	return (
		<Page>
			<ContentTable
				items={items}
				columns={eventColumns}
				showCreatedBy={false}
				showLastUpdated={false}
				showOrder={false}
				onRowClick={(item) => navigate(`/app/events/${item.id}`)}
				onMakeLive={(item) =>
					togglePublished({
						id: Number(item.id),
						published: true
					})
				}
				onMakeDraft={(item) =>
					togglePublished({
						id: Number(item.id),
						published: false
					})
				}
				onDelete={(item) => setItemToDelete(item)}
				createPath="/app/events/create"
				createLabel="Create Event"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete event?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setItemToDelete(null);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={() => {
					if (itemToDelete) {
						deleteEvent(Number(itemToDelete.id), {
							onSuccess: () => setItemToDelete(null)
						});
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Events;
